import moment from "moment/moment";
import {isNumber} from "../utils/number";
import {TimeFormat, TimetableDecorator} from "./TimetableDecorator";
import {LessonOccurrenceDecorator} from "./LessonOccurrenceDecorator";
import {isTwelveHourClock} from "../utils/date";
import {TFunction} from "i18next";
import {Lesson} from "../types/Lesson";
import {Subject} from "../types/Subject";

export class LessonWithOccurrenceIdAndDate {
    private readonly timetable: TimetableDecorator;
    private readonly lesson: Lesson;
    private readonly occurrence: LessonOccurrenceDecorator;
    private readonly subject: Subject | undefined | null;
    private readonly date: moment.Moment;

    constructor(
        timetable: TimetableDecorator,
        lesson: Lesson,
        occurrence: LessonOccurrenceDecorator,
        subject: Subject | undefined | null,
        date: moment.Moment
    ) {
        this.timetable = timetable;
        this.lesson = lesson;
        this.occurrence = occurrence;
        this.subject = subject;
        this.date = date;
    }

    getLesson(): Lesson {
        return this.lesson;
    }

    getOccurrence(): LessonOccurrenceDecorator {
        return this.occurrence;
    }

    getTimetable(): TimetableDecorator {
        return this.timetable
    }

    isRecurring(): boolean {
        return this.occurrence.toProps().isRecurring || Boolean(this.occurrence.toProps().recurringPattern)
    }

    getDate(): moment.Moment {
        return this.date;
    }

    getTitle(): string | undefined | null {
        if (this.subject) {
            return this.subject.name;
        } else {
            return this.lesson.title;
        }
    }

    getColor(): string | undefined | null {
        if (this.subject) {
            return this.subject.color;
        } else {
            return this.lesson.color;
        }
    }

    getRoom(): string | undefined | null {
        return this.lesson.room
    }

    getNote(): string | undefined | null {
        return this.lesson.note
    }

    formatStartEndTime(t: TFunction<"translation", undefined, "translation">): string {
        switch (this.timetable.getTimeFormat()) {
            case TimeFormat.PERIOD:
                const start = this.occurrence.toProps().timeStartInPeriods
                const end = this.occurrence.toProps().timeEndInPeriods

                if (start !== null && start !== undefined && isNumber(start) &&
                    end !== null && end !== undefined && isNumber(end)) {
                    return `${t("timetable.lesson.commit_dialog.occurrence.period_format", {val: start})} ➔ 
                        ${t("timetable.lesson.commit_dialog.occurrence.period_format", {val: end})}`
                } else {
                    return ""
                }

            default: {
                const start = this.occurrence.toProps().timeStartInMinutes
                const end = this.occurrence.toProps().timeEndInMinutes

                if (start !== null && start !== undefined && isNumber(start) &&
                    end !== null && end !== undefined && isNumber(end)) {
                    const startAsDate = moment()
                        .startOf('year')    // Daylight saving time
                        .set({"hour": start / 60, "minute": start % 60})
                    const endAsDate = moment()
                        .startOf('year')    // Daylight saving time
                        .set({"hour": end / 60, "minute": end % 60})
                    const timeFormat = isTwelveHourClock() ? "h:mm A" : "H:mm"
                    return `${startAsDate.format(timeFormat)} ➔ ${endAsDate.format(timeFormat)}`
                } else {
                    return ""
                }
            }
        }
    }

    // -----------------------------------------------------------------------------------------------------------------
    // Timetable specific functions

    getTopInUnits(): number | undefined | null {
        switch (this.timetable.getTimeFormat()) {
            case TimeFormat.PERIOD:
                return Math.max(0, (this.occurrence.toProps().timeStartInPeriods ?? 0) - 1)
            default: {
                const timeStart = this.occurrence.toProps().timeStartInMinutes
                if (timeStart && isNumber(timeStart)) {
                    return timeStart / 60.0
                }
            }
        }
    }

    getHeightInUnits(): number | undefined | null {
        let height: number | undefined | null = undefined

        switch (this.timetable.getTimeFormat()) {
            case TimeFormat.PERIOD:
                const start = this.occurrence.toProps().timeStartInPeriods
                const end = this.occurrence.toProps().timeEndInPeriods

                if (start !== null && start !== undefined && isNumber(start) &&
                    end !== null && end !== undefined && isNumber(end)) {
                    height = end - start + 1
                }
                break;
            default: {
                const start = this.occurrence.toProps().timeStartInMinutes
                const end = this.occurrence.toProps().timeEndInMinutes

                if (start !== null && start !== undefined && isNumber(start) &&
                    end !== null && end !== undefined && isNumber(end)) {
                    height = end / 60 - start / 60
                }
                break;
            }
        }

        return height
    }

    intersects(lesson: LessonWithOccurrenceIdAndDate): boolean {
        let start: number | undefined | null
        let end: number | undefined | null
        let otherStart: number | undefined | null
        let otherEnd: number | undefined | null

        switch (this.timetable.getTimeFormat()) {
            case TimeFormat.PERIOD:
                start = this.occurrence.toProps().timeStartInPeriods
                end = this.occurrence.toProps().timeEndInPeriods
                otherStart = lesson.occurrence.toProps().timeStartInPeriods
                otherEnd = lesson.occurrence.toProps().timeEndInPeriods
                break;
            default: {
                start = this.occurrence.toProps().timeStartInMinutes
                end = this.occurrence.toProps().timeEndInMinutes
                otherStart = lesson.occurrence.toProps().timeStartInMinutes
                otherEnd = lesson.occurrence.toProps().timeEndInMinutes
                break;
            }
        }

        if (start !== null && start !== undefined && isNumber(start) &&
            end !== null && end !== undefined && isNumber(end) &&
            otherStart !== null && otherStart !== undefined && isNumber(otherStart) &&
            otherEnd !== null && otherEnd !== undefined && isNumber(otherEnd)) {

            if (this.timetable.getTimeFormat() === TimeFormat.PERIOD) {
                return coerceAtLeast(start, otherStart) < (coerceAtMost(end, otherEnd) + 1)
            } else {
                return coerceAtLeast(start, otherStart) < (coerceAtMost(end, otherEnd))
            }
        } else {
            return false
        }
    }
}

function coerceAtLeast(value: number, other: number): number {
    return Math.max(value, other)
}

function coerceAtMost(value: number, other: number): number {
    return Math.min(value, other)
}